import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import type { Option } from "@sorcery/db";

import type { CardType, VariantType } from "~/types/card";
import type { CollectionItemDTOType } from "~/types/collection";

export enum ModalTypeEnum {
  CARDGALLERY = "card-gallery",
  SEARCH = "search",
  ADVANCEDCARDSEARCH = "advanced-card-search",
  ADVANCEDDECKSEARCH = "advanced-deck-search",
  CREATEDECK = "create-deck",
  COPYDECK = "copy-deck",
  EXPORTDECK = "export-deck",
  DELETEDECK = "delete-deck",
  DECKGALLERY = "deck-gallery",
  CREATEFOLDER = "create-folder",
  CREATECOLLECTIONITEMGALLERY = "create-collection-item",
  EDITCOLLECTIONITEMGALLERY = "edit-collection-item",
  BULKADDTOCOLLECTION = "bulk-add-to-collection",
  DELETECOLLECTION = "delete-collection",
  DELETECOLLECTIONCARDS = "delete-collection-cards",
  PRINTOUTCOLLECTIONTRACKERS = "printout-collection-trackers",
  FOLDERDELETE = "folder-delete",
  MOVEDECKTOFOLDER = "move-deck-to-folder",
  RENAMEFOLDER = "rename-folder",
}

export type AdvancedSearchPayload = {
  newFilters: Option[];
};

type ModalProps = {
  // lib and deckbuilder
  card?: CardType;
  variant?: VariantType;
  // search
  filters?: Option[];
  onConfirm?: (payload: AdvancedSearchPayload) => void;
  // collection
  item?: CollectionItemDTOType;
  // deck
  deckId?: string;
  deckName?: string;
  // folder
  folderId?: string;
};

type ModalContextProps = {
  closeModal: () => void;
  isModalOpen: boolean;
  modalType?: ModalTypeEnum;
  openModal: (modalType: ModalTypeEnum, props?: ModalProps) => void;
  props?: ModalProps;
};

const ModalContext = createContext<ModalContextProps>({} as ModalContextProps);

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalTypeEnum | undefined>(undefined);
  const [props, setProps] = useState<ModalProps>();
  const { events } = useRouter();

  function openModal(type: ModalTypeEnum, props?: ModalProps) {
    setIsModalOpen(true);
    setProps(props);
    setModalType(type);
  }

  function closeModal() {
    setIsModalOpen(false);
    setProps(undefined);
    setModalType(undefined);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // Handle back button press (and other route changes)
    const handleRouteChange = () => {
      if (isModalOpen) {
        closeModal();
      }
    };

    events.on("routeChangeStart", handleRouteChange);

    // Cleanup the event listener when the component unmounts
    return () => {
      events.off("routeChangeStart", handleRouteChange);
    };
  }, [isModalOpen, events]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const memoisedValue = useMemo(() => {
    return {
      openModal,
      closeModal,
      isModalOpen,
      modalType,
      props,
    };
  }, [isModalOpen, modalType, props]);

  return <ModalContext.Provider value={memoisedValue}>{children}</ModalContext.Provider>;
};

const useModal = () => useContext(ModalContext);
export { ModalProvider, useModal };
